import { pickOnPrimaryColor } from '@/themes/colorUtils';
import { opaqueContrastBaseOf } from '@/themes/surfaceOpacity';
// WEB-3447, заход 2: градиенты бренда — через дверь ленивого куска.
import { authorGradient, gradientTextColor } from '@/themes/styleKitDoor';

/**
 * WEB-1672 — оформление СТРОКИ ВАРИАНТА ОТВЕТА отдельно от заголовка вопроса.
 *
 * Репорт владельца: «ставлю красный в „Цвет текста внутри элемента“ — красным
 * становится всё сразу: и вопрос, и все варианты; покрасить их раздельно
 * нечем». Корень: заголовок (`<p style={{ color: primaryColor }}>`) и подпись
 * варианта (`resolveSelectedOptionColor(mode, primaryColor, isSelected)`,
 * которая для НЕвыбранного варианта возвращает ровно `primaryColor`) получали
 * ОДНО И ТО ЖЕ значение — потому и красились одинаково.
 *
 * ПОРЯДОК СИЛЫ СЛОЁВ ЦВЕТА ТЕКСТА ВАРИАНТА (шрам WEB-934 про слой-победитель;
 * решение по WEB-1674, оно же главный риск волны — «белым по белому»):
 *  1. `block.optionTextColor` — явный выбор автора, сильнее всего;
 *  2. авто-контраст по ЗАЛИВКЕ, которую автор задал ЭТОМУ ЖЕ состоянию
 *     варианта (`optionSelectedBackgroundColor` / `optionBackgroundColor`,
 *     WEB-1674) — тем же pickOnPrimaryColor, что у кнопки и поверхности
 *     элемента. Без него залитый белым выбранный вариант получил бы белый
 *     текст по режиму пака, то есть ровно дефект WEB-1060 заново;
 *  3. всё, что решало цвет до этих карточек: авто-подстройка выбранного
 *     варианта под подложку пака (WEB-1060, optionSelectedColor.ts) и цвет
 *     страницы для остальных.
 *
 * Слой автора отдаётся НАКЛАДКОЙ (`{ color }` либо пустой объект), которую
 * рендер спредит ПОСЛЕ прежнего `color:` — а не обёрткой вокруг него. Два
 * следствия, оба намеренные:
 *  • при пустом поле спредится `{}`, то есть у старой воронки не меняется ни
 *    пиксель, ни байт inline-стиля;
 *  • прежняя строка `color: resolveSelectedOptionColor(…)` остаётся в
 *    исходнике дословно — её стережёт регресс-тест WEB-1060 по исходному коду
 *    (PublicBlockRenderer.optionSelectedContrast.web1060.test.tsx), и
 *    переписывать его под новую правку нельзя.
 *
 * РЕЕСТРОВ ТИПОВ ЗДЕСЬ ДВА, И ЭТО НАМЕРЕННО (возврат приёмки 02.09).
 * Прежде список был один на два разных правила, и настройки ТЕКСТА варианта
 * унаследовали исключение, обоснованное только для ЗАЛИВКИ. Признак вхождения
 * у каждого свой:
 *  • OPTION_ROW_BLOCK_TYPES — «у варианта есть ПРЯМОУГОЛЬНИК» (заливка,
 *    WEB-1674);
 *  • OPTION_LABEL_BLOCK_TYPES — «у вариантов есть ПОДПИСИ» (их цвет WEB-1672 и
 *    кегль WEB-1673).
 * Класс дефекта тот же, что ловил комментарий 24.08 про
 * OPTION_ACCENT_BLOCK_TYPES: скопированный список приносит с собой чужое
 * обоснование, и «сделано по образцу» оставляет дефект живым.
 *
 * Оба списка — ЗЕРКАЛО канона бэка (backend/src/ai-funnels/block-schema/
 * option-row-style.ts): он же решает, каким блокам панель показывает
 * настройки. Разъезд ловят сторожа option-row-style.web1672.spec.ts и
 * option-label-style.web1673.spec.ts (читают ЭТОТ файл с диска) — не
 * переименовывать и не менять форму `new Set([...])` не обновив их.
 *
 * Почему список ЗАЛИВКИ уже, чем OPTION_ACCENT_BLOCK_TYPES (там ещё checkbox,
 * terms_checkbox, toggle, plan_selector):
 *  • `checkbox` / `terms_checkbox` / `toggle` — одиночная галочка, её подпись
 *    и ЕСТЬ текст блока; «цвет вариантов» там красил бы тот же единственный
 *    текст, что и «цвет текста внутри элемента» — два поля на одно свойство;
 *  • `plan_selector` — карточки тарифов со своей поверхностью
 *    (--color-bg-surface / --color-text-on-surface, инвариант WEB-966), это не
 *    строка варианта ответа;
 *  • `image_choice` — фото-карточки с подписью-плашкой поверх снимка (тон
 *    плашки задаёт imagery.* стиль-пака); прямоугольника варианта в том же
 *    смысле у него нет, ЗАЛИВКА была бы мёртвой настройкой (ловушка WEB-1496
 *    п.4). К подписям этот довод отношения не имеет — они у фото-карточек есть.
 */
export const OPTION_ROW_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'multi_step_question',
]);

/**
 * WEB-1673/WEB-1672 (возврат 02.09) — типы, у вариантов которых есть ПОДПИСИ:
 * им показываются «Цвет текста вариантов ответа» и «Размер текста вариантов
 * ответа, px».
 *
 * Живой прогон 01.09: у `image_choice` подписи под фотографиями не
 * управлялись из панели ничем — кегль был зашит в разметку числом (14), а
 * цвет делился с заголовком вопроса. Ровно та половина репорта владельца от
 * 24.08, которую предыдущая волна не закрыла, унаследовав исключение из
 * списка заливки.
 *
 * Одиночная галочка (checkbox/terms_checkbox/toggle) и карточки тарифов
 * (plan_selector) не входят по прежним доводам выше — там нет СПИСКА подписей
 * вариантов, отдельного от текста самого блока.
 */
export const OPTION_LABEL_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'multi_step_question',
  'image_choice',
]);

/** Поля оформления строки варианта, читаемые обоими рендерерами. */
export interface OptionRowFields {
  /** Цвет подписи варианта. Absent/пусто = как раньше (цвет заголовка/темы). */
  optionTextColor?: string;
  /**
   * WEB-1673 — кегль подписи варианта, px. Absent = как раньше: размер решает
   * `--option-size` стиль-пака.
   */
  optionFontSize?: number;
  /**
   * WEB-1674 — заливка НЕвыбранного варианта. Absent/пусто = как раньше: у
   * невыбранного варианта заливки нет вовсе (`background: undefined`).
   */
  optionBackgroundColor?: string;
  /**
   * WEB-1674 — заливка ВЫБРАННОГО варианта. Absent/пусто = как раньше: решает
   * стиль-пак через `--option-selected-bg`.
   */
  optionSelectedBackgroundColor?: string;
  /**
   * Решение владельца №2 (2026-10-08) — ГРАДИЕНТ выбранного варианта. Сильнее
   * кода цвета выше и режима стиль-пака. Absent/негодная строка = как раньше.
   */
  optionSelectedBackgroundGradient?: string;
  /**
   * Слово владельца 20.10.2026 — внешний вид СТРОКИ варианта (только типы
   * OPTION_ROW_BLOCK_TYPES). Absent у каждого = как раньше: тень, промежуток и
   * скругление решает стиль-пак, отметка (кружок/галочка) на месте.
   */
  optionShadowBlur?: number;
  optionShadowOffsetY?: number;
  optionShadowSpread?: number;
  optionShadowColor?: string;
  optionShadowOpacity?: number;
  optionGap?: number;
  optionRadius?: number;
  optionIndicator?: 'auto' | 'none';
  /**
   * Задание stylepack/option-indicator-align: положение отметки по высоте
   * строки. Absent и 'center' = как раньше (по центру строки); 'top' — у
   * верха содержимого, на уровне первой строки подписи.
   */
  optionIndicatorAlign?: 'center' | 'top';
  /**
   * Задание stylepack/text (слово владельца 20.10.2026): жирность подписи,
   * внутренний отступ плашки по двум осям, толщина и цвет рамки невыбранной
   * плашки. Absent у каждого = как раньше (решает стиль-пак).
   */
  optionFontWeight?: number;
  optionPaddingY?: number;
  optionPaddingX?: number;
  optionBorderWidth?: number;
  optionBorderColor?: string;
  /**
   * Задание stylepack/option-content-gap: зазор между отметкой, значком или
   * картинкой и текстом в строке варианта. Absent = прежние зашитые 10 px.
   */
  optionContentGap?: number;
  /**
   * Задание stylepack/option-min-height (слово владельца 21.10.2026): своя
   * минимальная высота строки, px; 0 — без минимума. Absent = высота пака.
   */
  optionMinHeight?: number;
  /**
   * Задание stylepack/selected-look (слово владельца 21.10.2026): вид
   * ВЫБРАННОЙ плашки сверх заливки — рамка, цвет подписи, цвет и сила тени
   * (размытие и сдвиг общие с невыбранной). Absent у каждого = как раньше
   * (решают стиль-пак и его режим выбора); заданное — сильнее режима выбора.
   */
  optionSelectedBorderColor?: string;
  optionSelectedBorderWidth?: number;
  optionSelectedTextColor?: string;
  optionSelectedShadowColor?: string;
  optionSelectedShadowOpacity?: number;
}

/**
 * Решение владельца №2 — типы, у которых есть «градиент выбранного варианта».
 * Зеркало `BLOCK_GRADIENT_FIELDS.optionSelectedBackgroundGradient` сервера
 * (backend/src/ai-funnels/block-schema/gradient-fields.ts; сторож
 * gradient-fields.ends.spec.ts читает ЭТОТ `new Set([...])` с диска).
 * Шире OPTION_ROW_BLOCK_TYPES на image_choice: у фото-карточки нет заливки
 * прямоугольника, но выбранная карточка градиентом — вид эталона.
 */
export const OPTION_GRADIENT_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'multi_step_question',
  'image_choice',
]);

/** Годный градиент выбранного варианта, если автор его задал. */
function authorGradientFor(
  fields: OptionRowFields,
  isSelected: boolean,
): string | undefined {
  return isSelected
    ? authorGradient(fields.optionSelectedBackgroundGradient)
    : undefined;
}

/** Заданная автором заливка ИМЕННО ЭТОГО состояния варианта, если она есть. */
function authorFillFor(
  fields: OptionRowFields,
  isSelected: boolean,
): string | undefined {
  const raw = isSelected
    ? fields.optionSelectedBackgroundColor
    : fields.optionBackgroundColor;
  return raw?.trim() || undefined;
}

/**
 * Накладка цвета подписи варианта: `{ color }` при заданном поле и ПУСТОЙ
 * объект иначе. Спредится в inline-стиль строки варианта ПОСЛЕ прежнего
 * `color:` — так слой автора побеждает, а пустое поле не добавляет в разметку
 * ничего вовсе.
 */
export function optionTextColorOverride(
  fields: OptionRowFields,
  isSelected: boolean,
): { color?: string } {
  // Задание stylepack/selected-look: свой цвет подписи ВЫБРАННОЙ плашки —
  // сильнее всего у выбранной (в том числе цвета всех подписей и режима пака).
  const ownSelected = isSelected
    ? fields.optionSelectedTextColor?.trim()
    : undefined;
  if (ownSelected) return { color: ownSelected };

  const own = fields.optionTextColor?.trim();
  if (own) return { color: own };

  // WEB-1674, слой 2 — АВТО-КОНТРАСТ ПО СВОЕЙ ЗАЛИВКЕ.
  //
  // Заливка варианта и цвет его текста — связанная пара. Цвет текста
  // ВЫБРАННОГО варианта считается по РЕЖИМУ стиль-пака (WEB-1060,
  // optionSelectedColor.ts): у 'filled'-пака это var(--color-on-primary),
  // обычно белый. Залей автор выбранный вариант белым — и текст остался бы
  // белым на белом, ровно тот дефект, ради которого писали WEB-1060. Поэтому
  // к СВОЕЙ заливке автора доклеивается контраст той же формулой, что у
  // кнопки, значка гарантии и поверхности элемента (pickOnPrimaryColor,
  // blockInnerSurface.tsx) — своего замера контраста здесь нет.
  //
  // Считается по заливке ИМЕННО ЭТОГО состояния: заливка выбранного не должна
  // решать цвет невыбранного и наоборот.
  //
  // Нераспознанный код цвета (старый снапшот, ручная правка) авто-контраст НЕ
  // включает: гадать хуже, чем оставить прежнее поведение — hexLuminance на
  // непарсимой строке возвращает 1 и молча выдал бы тёмный текст.
  //
  // WEB-1859: пригодность заливки решает ОБЩИЙ помощник
  // (`opaqueContrastBaseOf`, themes/surfaceOpacity.ts), а не здешний регексп.
  // Регексп пропускал восьмизначный код с ЛЮБОЙ прозрачностью — то есть по
  // плёнке, сквозь которую видна страница, текст красился как по сплошной
  // заливке, и на одном экране надпись кнопки и подпись варианта выходили
  // разных тонов. Порог теперь один на все помощники.
  // Решение владельца №2: градиент выбранного сильнее кода цвета — и
  // контраст считается по нему (первый стоп, тот же приём, что у кнопки
  // темы 'gradient'). Полупрозрачный стоп опорой не годится — дальше.
  const gradientColor = gradientTextColor(
    authorGradientFor(fields, isSelected),
  );
  if (gradientColor) return { color: gradientColor };

  const fill = authorFillFor(fields, isSelected);
  const base = opaqueContrastBaseOf(fill);
  if (base) return { color: pickOnPrimaryColor(base) };

  // Слой 3 — ничего своего не задано: накладка пуста, работает то, что было до
  // этих карточек (авто-подстройка WEB-1060 / цвет страницы).
  return {};
}

/**
 * WEB-1674 — накладка ЗАЛИВКИ прямоугольника варианта.
 *
 * До неё подложка бралась ТОЛЬКО из переменных стиль-пака
 * (`var(--option-selected-bg, …)` у выбранного, `undefined` у невыбранного —
 * заливки у него не было в принципе), и повлиять на это автор не мог ничем,
 * кроме смены пака всей воронки.
 *
 * Накладка спредится ПОСЛЕ `background:` и вытесняет значение пака. Пустое
 * поле спредит `{}` — заливка остаётся ровно прежней.
 */
export function optionBackgroundOverride(
  fields: OptionRowFields,
  isSelected: boolean,
): { background?: string } {
  // Решение владельца №2: градиент выбранного — сильнее кода цвета.
  const gradient = authorGradientFor(fields, isSelected);
  if (gradient) return { background: gradient };
  const fill = authorFillFor(fields, isSelected);
  return fill ? { background: fill } : {};
}

/**
 * WEB-1673 — накладка КЕГЛЯ подписи варианта.
 *
 * Прежнее поле «Размер текста внутри элемента» (`innerFontSize`) ставит
 * `fontSize` на обёртку блока в расчёте на наследование — а строка варианта
 * наследовать не может: у неё размер задан ЯВНО,
 * `fontSize: 'var(--option-size, inherit)'`. Переменную `--option-size`
 * объявляет стиль-пак воронки (themes/stylePackToStyle.ts) ВЫШЕ по дереву,
 * поэтому конкретное значение у строки есть всегда и `inherit` не срабатывает
 * НИКОГДА, пока пак применён. То есть настройка не «иногда не работала» — она
 * системно перебивалась паком.
 *
 * Победа над паком — механикой CSS, а не гонкой специфичности: накладка несёт
 * СОБСТВЕННЫЙ литеральный `fontSize` и спредится ПОСЛЕ строки с
 * `var(--option-size, …)`, полностью её вытесняя. Пустое поле спредит `{}` —
 * размер по-прежнему решает пак, старая воронка не меняется ни на байт.
 */
export function optionFontSizeOverride(fields: OptionRowFields): {
  fontSize?: number;
} {
  return typeof fields.optionFontSize === 'number'
    ? { fontSize: fields.optionFontSize }
    : {};
}

/**
 * Слово владельца 20.10.2026 — границы ползунков внешнего вида строки
 * варианта. ЗЕРКАЛО канона сервера `OPTION_ROW_LOOK_BOUNDS`
 * (backend/src/ai-funnels/block-schema/option-row-style.ts); разъезд ловит
 * сторож `option-row-style.look-ends.spec.ts` — он читает ЭТОТ объект с
 * диска, форму `{ min: N, max: N }` не менять, не обновив его.
 */
export const OPTION_ROW_LOOK_BOUNDS = {
  optionShadowBlur: { min: 0, max: 48 },
  optionShadowOffsetY: { min: -24, max: 24 },
  optionShadowSpread: { min: -16, max: 16 },
  optionShadowOpacity: { min: 0, max: 1 },
  optionGap: { min: 0, max: 64 },
  optionRadius: { min: 0, max: 64 },
  optionFontWeight: { min: 100, max: 900 },
  optionPaddingY: { min: 0, max: 48 },
  optionPaddingX: { min: 0, max: 48 },
  optionBorderWidth: { min: 0, max: 8 },
  optionContentGap: { min: 0, max: 40 },
  optionMinHeight: { min: 0, max: 200 },
} as const;

/**
 * Задание stylepack/selected-look — границы ползунков ВЫБРАННОЙ плашки.
 * ЗЕРКАЛО канона сервера `OPTION_SELECTED_LOOK_BOUNDS`
 * (backend/src/ai-funnels/block-schema/option-row-style.ts); разъезд схемы,
 * зеркала и панели ловит `option-row-style.selected-ends.spec.ts` — он читает
 * ЭТОТ объект с диска, форму `{ min: N, max: N }` не менять, не обновив его.
 */
export const OPTION_SELECTED_LOOK_BOUNDS = {
  optionSelectedBorderWidth: { min: 0, max: 8 },
  optionSelectedShadowOpacity: { min: 0, max: 1 },
} as const;

/**
 * Что тень берёт за поле, которого автор ещё не трогал, когда тронул
 * соседнее (например, подвинул только «Размытие»). Те же числа панель
 * показывает на нетронутых ползунках — обещание панели и вид совпадают.
 * Мягкая серая тень: заметна на светлом и тёмном фоне, не спорит с рамкой.
 */
export const OPTION_SHADOW_DEFAULTS = {
  blur: 12,
  offsetY: 4,
  spread: 0,
  color: '#000000',
  opacity: 0.15,
} as const;

const SHADOW_FIELDS = [
  'optionShadowBlur',
  'optionShadowOffsetY',
  'optionShadowSpread',
  'optionShadowColor',
  'optionShadowOpacity',
] as const;

/** Поля тени ВЫБРАННОЙ плашки (задание stylepack/selected-look). */
const SELECTED_SHADOW_FIELDS = [
  'optionSelectedShadowColor',
  'optionSelectedShadowOpacity',
] as const;

/** Разбор строгого кода цвета (#rgb/#rgba/#rrggbb/#rrggbbaa); иное — null. */
function parseHex(
  hex: string | undefined,
): { r: number; g: number; b: number; a: number } | null {
  const raw = hex?.trim().toLowerCase();
  if (!raw || !/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(raw)) {
    return null;
  }
  const body = raw.slice(1);
  const full =
    body.length <= 4
      ? body
          .split('')
          .map((c) => c + c)
          .join('')
      : body;
  const byte = (i: number) => parseInt(full.slice(i, i + 2), 16);
  return {
    r: byte(0),
    g: byte(2),
    b: byte(4),
    a: full.length === 8 ? byte(6) / 255 : 1,
  };
}

/** Число в границах, иначе умолчание (страховка от старых/чужих данных). */
function within(
  value: number | undefined,
  bounds: { min: number; max: number },
  fallback: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/**
 * CSS-значение тени строки варианта или undefined, если ни одного поля тени
 * нет. Цвет и прозрачность сводятся в rgba(): альфа кода цвета (#rrggbbaa)
 * умножается на ползунок «Прозрачность». Негодный код цвета — цвет по
 * умолчанию: мусор в inline-стиль публичной страницы не уходит.
 *
 * Задание stylepack/selected-look: у ВЫБРАННОЙ плашки (`selected`) свои цвет
 * и сила тени сильнее общих; размытие, сдвиг и растяжение — общие. Поля
 * выбранного у невыбранной не значат ничего: тень только из них — тень
 * появляется лишь при выборе (форма — по умолчанию).
 */
export function optionShadowValue(
  fields: OptionRowFields,
  selected = false,
): string | undefined {
  const own = SHADOW_FIELDS.some((field) => fields[field] !== undefined);
  const ownSelected =
    selected &&
    SELECTED_SHADOW_FIELDS.some((field) => fields[field] !== undefined);
  if (!own && !ownSelected) {
    return undefined;
  }
  const b = OPTION_ROW_LOOK_BOUNDS;
  const d = OPTION_SHADOW_DEFAULTS;
  const blur = within(fields.optionShadowBlur, b.optionShadowBlur, d.blur);
  const y = within(fields.optionShadowOffsetY, b.optionShadowOffsetY, d.offsetY);
  const spread = within(
    fields.optionShadowSpread,
    b.optionShadowSpread,
    d.spread,
  );
  const sharedOpacity = within(
    fields.optionShadowOpacity,
    b.optionShadowOpacity,
    d.opacity,
  );
  const opacity = selected
    ? within(
        fields.optionSelectedShadowOpacity,
        OPTION_SELECTED_LOOK_BOUNDS.optionSelectedShadowOpacity,
        sharedOpacity,
      )
    : sharedOpacity;
  const color =
    (selected ? parseHex(fields.optionSelectedShadowColor) : null) ??
    parseHex(fields.optionShadowColor) ??
    parseHex(d.color)!;
  const alpha = Math.round(color.a * opacity * 1000) / 1000;
  return `0px ${y}px ${blur}px ${spread}px rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

/**
 * Накладка тени строки варианта — спредится ПОСЛЕ
 * `boxShadow: 'var(--option-ring, none)'`. У пака с кольцом выбора
 * (shape.selectionMode === 'ring') рамка обнулена и форму варианта держит
 * кольцо-тень: его нельзя вытеснить, поэтому оно идёт вторым слоем. У
 * остальных паков `--option-ring` равно `none`, и в список теней его ставить
 * нельзя (весь box-shadow стал бы негодным). Пусто — `{}`, вид прежний.
 */
export function optionShadowOverride(
  fields: OptionRowFields,
  ring: boolean,
  selected = false,
): { boxShadow?: string } {
  const shadow = optionShadowValue(fields, selected);
  if (!shadow) return {};
  return { boxShadow: ring ? `${shadow}, var(--option-ring)` : shadow };
}

/**
 * Задание stylepack/option-own-border: тень строки варианта целиком — ОДНА
 * дверь для `var(--option-ring…)` в строках вариантов (сторож — обход
 * исходников в optionRowStyle.ownBorder.test.ts). Своя рамка блока
 * (optionOwnBorderSet) гасит кольцо пака у НЕвыбранного варианта: иначе
 * рисуются и рамка автора, и кольцо. Выбранный и строка без своей рамки —
 * как прежде: кольцо пака, тень автора — первым слоем.
 */
export function optionRowShadow(
  fields: OptionRowFields,
  selected: boolean,
  ring: boolean,
): { boxShadow: string } {
  const off = !selected && optionOwnBorderSet(fields);
  // Шов сведения option-own-border × selected-look: тень отмеченной — своим
  // цветом и силой (selected), кольцо — только если у невыбранного нет своей
  // рамки (off).
  const own = optionShadowOverride(fields, ring && !off, selected).boxShadow;
  return { boxShadow: own ?? (off ? 'none' : 'var(--option-ring, none)') };
}

/** Накладка промежутка между вариантами (на контейнер списка). */
export function optionGapOverride(fields: OptionRowFields): { gap?: number } {
  return typeof fields.optionGap === 'number' ? { gap: fields.optionGap } : {};
}

/**
 * Задание stylepack/option-content-gap — накладка зазора между частями
 * СТРОКИ варианта (отметка, значок или картинка, текст) и стопки «значок над
 * подписью». Спредится ПОСЛЕ зашитого `gap` строки: ключ встаёт на его место,
 * порядок свойств тот же. Нет поля или нечисло — `{}`, разметка прежняя.
 */
export function optionContentGapOverride(fields: OptionRowFields): {
  gap?: number;
} {
  const gap = ownInt(
    fields.optionContentGap,
    OPTION_ROW_LOOK_BOUNDS.optionContentGap,
  );
  return gap === undefined ? {} : { gap };
}

/** Накладка скругления строки варианта — ПОСЛЕ радиуса стиль-пака. */
export function optionRadiusOverride(fields: OptionRowFields): {
  borderRadius?: number;
} {
  return typeof fields.optionRadius === 'number'
    ? { borderRadius: fields.optionRadius }
    : {};
}

/** «Без отметки»: кружка/галочки у варианта не видно. */
export function optionIndicatorHidden(fields: OptionRowFields): boolean {
  return fields.optionIndicator === 'none';
}

/**
 * Стиль спрятанной отметки. Поле ввода остаётся в разметке: клик по строке
 * (label) по-прежнему выбирает вариант, клавиатура и экранный диктор видят
 * его как раньше — исчезает только кружок/галочка на экране. Выбранный
 * вариант отличается заливкой и рамкой, которые и сейчас меняются при выборе.
 */
export const OPTION_INDICATOR_HIDDEN_STYLE = {
  position: 'absolute',
  opacity: 0,
  width: 1,
  height: 1,
  margin: 0,
  pointerEvents: 'none',
} as const;

/**
 * Задание stylepack/option-indicator-align (слово владельца 21.10.2026:
 * повторять чужую воронку в точности). ЗЕРКАЛО канона сервера
 * `OPTION_INDICATOR_ALIGN_VALUES` (backend/src/ai-funnels/block-schema/
 * option-row-style.ts); разъезд ловит option-indicator-align.ends.spec.ts —
 * он читает ЭТОТ массив с диска.
 */
export const OPTION_INDICATOR_ALIGN_VALUES = ['center', 'top'] as const;

/**
 * Накладка на САМО поле отметки (кружок/галочку): 'top' ставит его к верху
 * содержимого строки (`alignSelf: 'flex-start'`), остальное в строке —
 * подпись, значки — остаётся по центру, как было. У варианта с описанием в
 * несколько строк подпись занимает всю высоту, и отметка встаёт на уровень
 * её первой строки. Нет поля, 'center' и чужое значение — `{}`: разметка
 * прежняя байт в байт.
 */
export function optionIndicatorAlignOverride(fields: OptionRowFields): {
  alignSelf?: 'flex-start';
} {
  return fields.optionIndicatorAlign === 'top'
    ? { alignSelf: 'flex-start' }
    : {};
}

/** Строке со спрятанной отметкой — опора для `position: absolute` поля. */
export function optionRowIndicatorOverride(fields: OptionRowFields): {
  position?: 'relative';
} {
  return optionIndicatorHidden(fields) ? { position: 'relative' } : {};
}

/** Целое в границах или undefined (нечисло — не в стиль). */
function ownInt(
  value: number | undefined,
  bounds: { min: number; max: number },
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.round(Math.min(bounds.max, Math.max(bounds.min, value)));
}

/**
 * Задание stylepack/text: внутренний отступ плашки варианта — накладка ПОСЛЕ
 * отступа стиль-пака. Ось без своего значения берёт `fallbackY`/`fallbackX`
 * (переменную пака или прежнее число этого рендера): одна сокращённая запись
 * `padding`, без смеси с `paddingTop`/`paddingLeft`. Пусто — `{}`.
 */
export function optionPaddingOverride(
  fields: OptionRowFields,
  fallbackY: string,
  fallbackX: string,
): { padding?: string } {
  const b = OPTION_ROW_LOOK_BOUNDS;
  const y = ownInt(fields.optionPaddingY, b.optionPaddingY);
  const x = ownInt(fields.optionPaddingX, b.optionPaddingX);
  if (y === undefined && x === undefined) return {};
  return {
    padding: `${y === undefined ? fallbackY : `${y}px`} ${x === undefined ? fallbackX : `${x}px`}`,
  };
}

/** Своя рамка варианта задана: толщина числом (и 0) или цвет (любой). */
export function optionOwnBorderSet(fields: OptionRowFields): boolean {
  return (
    ownInt(fields.optionBorderWidth, OPTION_ROW_LOOK_BOUNDS.optionBorderWidth) !==
      undefined || fields.optionBorderColor !== undefined
  );
}

/** Литерал `Npx` → N; переменная или иное — null. */
function literalPx(value: string): number | null {
  const m = /^(\d+(?:\.\d+)?)px$/.exec(value.trim());
  return m ? Number(m[1]) : null;
}

/**
 * Задание stylepack/selected-look: отступ ВЫБРАННОЙ плашки, у которой рамка
 * толще или тоньше невыбранной, — разница толщин снимается с отступа (или
 * добавляется к нему), и подпись с соседями не прыгает при выборе. Внешний
 * размер плашки тот же: рамка растёт внутрь. Числа известны — литерал, иначе
 * `calc` с переменной пака; ниже нуля отступ не уходит.
 */
function compensatedPadding(
  fields: OptionRowFields,
  pad: { y: string; x: string },
  selectedPx: number,
  unselected: number | string,
): string | undefined {
  if (unselected === selectedPx) return undefined;
  const b = OPTION_ROW_LOOK_BOUNDS;
  const ownY = ownInt(fields.optionPaddingY, b.optionPaddingY);
  const ownX = ownInt(fields.optionPaddingX, b.optionPaddingX);
  const axis = (own: number | undefined, fallback: string) => {
    const base = own ?? literalPx(fallback);
    if (base !== null && typeof unselected === 'number') {
      return `${Math.max(0, base - (selectedPx - unselected))}px`;
    }
    const start = own === undefined ? fallback : `${own}px`;
    const back =
      typeof unselected === 'number' ? `${unselected}px` : unselected;
    return `max(0px, calc(${start} - ${selectedPx}px + ${back}))`;
  };
  return `${axis(ownY, pad.y)} ${axis(ownX, pad.x)}`;
}

/**
 * Задание stylepack/text: рамка плашки варианта — накладка ПОСЛЕ рамки
 * стиль-пака. Своя толщина — у всех плашек; свой цвет — у НЕвыбранной: у
 * выбранной рамку красит выбор пака (`base.selectedColor`), иначе выбранный
 * вариант не отличить (особенно «без отметки»). У строки без своего цвета
 * выбора (`selectedColor` не задан) свой цвет — у обеих. Негодный код цвета в
 * стиль не уходит — остаётся цвет пака. Пусто — `{}`.
 */
export function optionBorderOverride(
  fields: OptionRowFields,
  selected: boolean,
  base: { width: string; color: string; selectedColor?: string },
  pad?: { y: string; x: string },
): { border?: string; padding?: string } {
  const width = ownInt(
    fields.optionBorderWidth,
    OPTION_ROW_LOOK_BOUNDS.optionBorderWidth,
  );
  if (selected) {
    const own = optionSelectedBorder(fields, base, width);
    if (own) {
      const padding = pad
        ? compensatedPadding(
            fields,
            pad,
            own.px,
            // Толщина невыбранной: своя, литерал строки (доля — '2px') или
            // переменная пака.
            width ?? literalPx(base.width) ?? base.width,
          )
        : undefined;
      return padding ? { border: own.border, padding } : { border: own.border };
    }
  }
  const own = parseHex(fields.optionBorderColor)
    ? fields.optionBorderColor!.trim()
    : undefined;
  if (!optionOwnBorderSet(fields)) return {};
  const color =
    selected && base.selectedColor ? base.selectedColor : (own ?? base.color);
  return {
    border: `${width === undefined ? base.width : `${width}px`} solid ${color}`,
  };
}

/** Акцент темы — рамка выбранной у строки без своего цвета выбора. */
const ACCENT_BORDER = 'var(--color-primary, #1677ff)';

/**
 * Задание stylepack/selected-look: своя рамка ВЫБРАННОЙ плашки или null,
 * если полей выбранного нет. Толщина без цвета — цвет акцента пака (выбор
 * пака `base.selectedColor`, у строки без него — акцент темы); цвет без
 * толщины — толщина своей невыбранной рамки, а если её нет (или 0) — 2 px.
 * Негодный код цвета — как «цвета нет». Сильнее режима выбора пака.
 */
function optionSelectedBorder(
  fields: OptionRowFields,
  base: { selectedColor?: string },
  unselectedPx: number | undefined,
): { border: string; px: number } | null {
  const ownWidth = ownInt(
    fields.optionSelectedBorderWidth,
    OPTION_SELECTED_LOOK_BOUNDS.optionSelectedBorderWidth,
  );
  if (ownWidth === undefined && fields.optionSelectedBorderColor === undefined) {
    return null;
  }
  const color = parseHex(fields.optionSelectedBorderColor)
    ? fields.optionSelectedBorderColor!.trim()
    : (base.selectedColor ?? ACCENT_BORDER);
  const px = ownWidth ?? (unselectedPx ? unselectedPx : 2);
  return { border: `${px}px solid ${color}`, px };
}

/**
 * Задание stylepack/text: жирность подписи варианта — ПОСЛЕ `--option-weight`
 * пака. Кусок подписи с пометкой bold остаётся 700 (RichTextContent).
 */
export function optionFontWeightOverride(fields: OptionRowFields): {
  fontWeight?: number;
} {
  const w = ownInt(fields.optionFontWeight, OPTION_ROW_LOOK_BOUNDS.optionFontWeight);
  return w === undefined ? {} : { fontWeight: Math.round(w / 100) * 100 };
}

/**
 * Задание stylepack/option-min-height (слово владельца 21.10.2026: повторять
 * чужую воронку в точности — у Blinkist варианты 48, а пак blue-gradient
 * даёт 97). Минимальная высота строки варианта: своё число блока, иначе
 * `fallback` — то, что рендер ставил до поля (переменная пака
 * `var(--option-h, auto)`). Своё число перебивает пак только у этого блока:
 * переменная пака на обёртке страницы не трогается. 0 — значащее: минимума
 * нет, высота по содержимому и отступам. Чужое значение зажимается в
 * границы, нечисло — `fallback`.
 */
export function optionMinHeightOf<T>(
  fields: OptionRowFields,
  fallback: T,
): number | T {
  const own = ownInt(
    fields.optionMinHeight,
    OPTION_ROW_LOOK_BOUNDS.optionMinHeight,
  );
  return own === undefined ? fallback : own;
}

/**
 * Та же высота накладкой — для строки, у которой своего `minHeight` не было
 * (multi_step_question): пусто — `{}`, разметка прежняя.
 */
export function optionMinHeightOverride(fields: OptionRowFields): {
  minHeight?: number;
} {
  const own = optionMinHeightOf(fields, undefined);
  return own === undefined ? {} : { minHeight: own };
}
